import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import StatusRibbon, { STATUS_RIBBON_CLEARANCE } from '../StatusRibbon/StatusRibbon';
import StickyHeaderBar from '../StickyHeaderBar/StickyHeaderBar';

export interface DetailHeaderProps {
  /** Drives the ribbon along the top edge. */
  isActive?: boolean | null;
  /** For entities with no Active/Inactive state (entity groups): no ribbon, and no room kept for it. */
  hideStatus?: boolean;
  /** The entity's name. A string gets the hover tooltip once it is clamped. */
  title: React.ReactNode;
  /** The line under the title, usually `<em>Offer ID: </em>123`. */
  subtitle?: React.ReactNode;
  /** Controls ahead of the title block: a back link, a panel toggle. */
  leading?: React.ReactNode;
  /** Buttons at the top of the right column: audit, locations, preview. */
  actions?: React.ReactNode;
  /** The Active/Inactive control, under the name and id. */
  toggle?: React.ReactNode;
  /** Configuration fields, under the action buttons in the right column. */
  children?: React.ReactNode;
  /** Pin under the app bar while the page scrolls, laid out as a single row. */
  sticky?: boolean;
  sx?: SxProps<Theme>;
}

/** Lines the title may take before it is cut with an ellipsis. */
export const TITLE_MAX_LINES = 3;

/** True once the element's content overflows its clamped box, re-checked as it resizes. */
function useIsClamped(text: React.ReactNode) {
  const ref = React.useRef<HTMLElement | null>(null);
  const [clamped, setClamped] = React.useState(false);
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const check = () => setClamped(element.scrollHeight > element.clientHeight + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);
  return { ref, clamped };
}

/**
 * The bar at the top of every detail page, laid out the same way everywhere: two columns.
 *
 *   ┌ ribbon ────────────────────────────────────────────────────────┐
 *   │ Title (up to 3 lines, hover for all)                [actions…] │
 *   │ Offer ID: 123                                ARIA Offer ▾  …   │
 *   │ [● Active]                                                     │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Identity on the left (name, id, toggle), controls on the right (buttons over configuration
 * fields). The toggle is anchored under the name, so it sits in the same place on every page
 * and at every width; when the row gets tight the whole controls column drops under the name
 * as one block instead of buttons and fields interleaving.
 */
export default function DetailHeader({
  isActive,
  hideStatus = false,
  title,
  subtitle,
  leading,
  actions,
  toggle,
  children,
  sticky = false,
  sx,
}: DetailHeaderProps) {
  const { ref, clamped } = useIsClamped(title);
  const hasConfigs = React.Children.count(children) > 0;

  const heading = (
    <Typography
      ref={ref}
      variant={sticky ? 'h5' : 'h4'}
      component='h3'
      sx={{
        display: '-webkit-box',
        // The sticky bar is a single row, so its name gets one line; hover shows the rest.
        WebkitLineClamp: sticky ? 1 : TITLE_MAX_LINES,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        overflowWrap: 'anywhere',
      }}
    >
      {title}
    </Typography>
  );

  const body = (
    <>
      {!hideStatus && <StatusRibbon isActive={isActive} />}
      <Box
        data-detail-header
        sx={{
          px: 2,
          py: 1.5,
          pt: hideStatus ? 1.5 : STATUS_RIBBON_CLEARANCE,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          columnGap: 3,
          rowGap: 1.5,
        }}
      >
        {/* Identity column: leading controls, then the name, its id and the toggle stacked. The
            column may shrink (`minWidth: 0`) so a long name clamps rather than shoving the controls off. */}
        <Box
          data-detail-header-identity
          sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', alignItems: 'flex-start', columnGap: 1.5 }}
        >
          {leading && (
            <Stack direction='row' spacing={0.5} sx={{ alignItems: 'center', flex: '0 0 auto' }}>
              {leading}
            </Stack>
          )}
          <Box sx={{ minWidth: 0 }}>
            {/* An empty title disables the tooltip, so it only appears once the clamp bites. */}
            <Tooltip title={clamped ? title : ''} enterDelay={300}>
              {heading}
            </Tooltip>
            {subtitle && (
              <Typography variant='h6' component='div'>
                {subtitle}
              </Typography>
            )}
            {/* Under the id, as the last thing in the identity block: the only control on this
                side, so it reads as part of the name rather than as one of the actions. */}
            {toggle && <Box sx={{ mt: 1.5 }}>{toggle}</Box>}
          </Box>
        </Box>
        {/* Controls column, right-aligned: the action buttons on top, the configuration fields
            beneath them. It drops under the identity column as one block when the row is tight. */}
        {(actions || hasConfigs) && (
          <Box
            data-detail-header-controls
            sx={{ flex: '0 1 auto', ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1.5 }}
          >
            {actions && (
              // `useFlexGap`: spacing as a gap rather than child margins, so the reset below can
              // zero the margins the shared modal triggers set on themselves (they were written
              // for the floated rows they used to sit in) without also erasing the spacing.
              // `nowrap` keeps a label like "Copy Sequence" on one line when the row gets tight.
              <Stack
                data-detail-header-actions
                useFlexGap
                direction='row'
                spacing={1}
                sx={{
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  '& .MuiButton-root': { whiteSpace: 'nowrap', m: 0, float: 'none' },
                }}
              >
                {actions}
              </Stack>
            )}
            {hasConfigs && (
              <Box
                data-detail-header-configs
                sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', columnGap: 2, rowGap: 1.5 }}
              >
                {children}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </>
  );

  // The sticky bar stays pinned over the form, so it spends one row, not three:
  //
  //   ┌ ribbon ────────────────────────────────────────────────────────────────────┐
  //   │ [● Active]  Title (one line, hover for all)    [ARIA]  [fields]  [actions…] │
  //   │             Offer ID: 123                                                   │
  //   └─────────────────────────────────────────────────────────────────────────────┘
  //
  // Left to right: leading controls, the toggle, the name with its id under it, then the
  // right-aligned group: the configuration fields (the first child leads it), then the buttons.
  // Below lg the row may wrap rather than crush the name; from lg it holds as one line.
  const row = (
    <>
      {!hideStatus && <StatusRibbon isActive={isActive} />}
      <Box
        data-detail-header
        data-detail-header-row
        sx={{
          px: 2,
          py: 1,
          pt: hideStatus ? 1 : STATUS_RIBBON_CLEARANCE,
          display: 'flex',
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          alignItems: 'center',
          columnGap: 2,
          rowGap: 1,
        }}
      >
        {leading && (
          <Stack direction='row' spacing={0.5} sx={{ alignItems: 'center', flex: '0 0 auto' }}>
            {leading}
          </Stack>
        )}
        {toggle && <Box sx={{ flex: '0 0 auto' }}>{toggle}</Box>}
        {/* Takes what is left and shrinks first, so a long name truncates instead of pushing
            the controls off the bar. */}
        <Box data-detail-header-identity sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <Tooltip title={clamped ? title : ''} enterDelay={300}>
            {heading}
          </Tooltip>
          {subtitle && (
            <Typography variant='body2' component='div' color='text.secondary' noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>
        {hasConfigs && (
          <Box
            data-detail-header-configs
            sx={{ display: 'flex', alignItems: 'center', columnGap: 2, flex: '0 1 auto', minWidth: 0 }}
          >
            {children}
          </Box>
        )}
        {actions && (
          <Stack
            data-detail-header-actions
            useFlexGap
            direction='row'
            spacing={1}
            sx={{
              alignItems: 'center',
              flex: '0 0 auto',
              '& .MuiButton-root': { whiteSpace: 'nowrap', m: 0, float: 'none' },
            }}
          >
            {actions}
          </Stack>
        )}
      </Box>
    </>
  );

  // A card in its own right, clipping the ribbon to its corners. Not an MUI Card: the layout's
  // card-rhythm rule keys on Paper, and this bar spaces itself with `mb` instead so the sticky
  // variant (whose wrapper is not a Paper either) lands the same distance from the content.
  const surface: SxProps<Theme> = [
    {
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      boxShadow: 1,
      overflow: 'hidden',
      mb: 3,
    },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  if (sticky) {
    return (
      <StickyHeaderBar bleed={3} sx={surface}>
        {row}
      </StickyHeaderBar>
    );
  }
  return <Box sx={surface}>{body}</Box>;
}
